/**
 * Rive layer for the SicBo port.
 *
 * Loads three artboards onto three stacked canvases — mirroring
 *   packages/amgsicbo_v2/lib/presentation/rive/rive_controller_manager.dart:
 *
 *     main     → sicboanimation.riv    artboard "Main"      (girl, BG, balance text)
 *     topLayer → sicboanimation.riv    artboard "TopLayer"  (dice bowl, BetPanel)
 *     betting  → sicbobetting_v2.riv   artboard "Main"      (bet zones, chip picker)
 *
 * State machine is `animationManager` for all three artboards. There is NO
 * separate WinLose canvas in V2 — win/lose is rendered by a DOM panel.
 */
import {
  Rive,
  Layout,
  Fit,
  Alignment,
  EventType,
  StateMachineInputType,
} from '@rive-app/webgl2';

import { debugLog, warn } from '../util/logger';
import {
  BetPanelState,
  DiceBowlState,
  MainInputs,
  RiveEventNames,
  TextInputs,
  TopLayerInputs,
} from './riveInputs';

// ----------------------------------------------------------------------------

type AnyInput =
  | { type: 'number'; name: string; set(v: number): void; get(): number }
  | { type: 'boolean'; name: string; set(v: boolean): void; get(): boolean }
  | { type: 'trigger'; name: string; fire(): void };

interface RiveTarget {
  key: TargetKey;
  file: string;
  stateMachine: string;
  instance: Rive;
  inputs: Map<string, AnyInput>;
}

export type TargetKey = 'main' | 'topLayer' | 'betting';

const FILES: Record<TargetKey, string> = {
  main: 'sicboanimation.riv',
  topLayer: 'sicboanimation.riv',
  betting: 'sicbobetting_v2.riv',
};

const ARTBOARDS: Record<TargetKey, string> = {
  main: 'Main',
  topLayer: 'TopLayer',
  betting: 'Main',
};

const STATE_MACHINES: Record<TargetKey, string> = {
  main: 'animationManager',
  topLayer: 'animationManager',
  betting: 'animationManager',
};

// ----------------------------------------------------------------------------

export interface RiveLayerOptions {
  canvases: Record<TargetKey, HTMLCanvasElement>;
  assetBase?: string;
}

export type RiveEventTopic =
  | 'ready'
  | 'allControllersReady'
  | 'placeBetClick'
  | 'evtClickChips'
  | 'evtClearClick'
  | 'evtButtonPlayClick'
  | 'UIButtonClick'
  | 'evtResultStop'
  | 'evtSound'
  | 'evtBGM'
  | 'DiceBowlEnd';

type RiveEventCallback = (data: Record<string, unknown>) => void;

// ----------------------------------------------------------------------------

export class RiveLayer {
  private readonly canvases: Record<TargetKey, HTMLCanvasElement>;
  private readonly assetBase: string;
  private readonly targets: Map<TargetKey, RiveTarget> = new Map();
  private readonly listeners: Map<string, Set<RiveEventCallback>> = new Map();

  constructor(opts: RiveLayerOptions) {
    this.canvases = opts.canvases;
    this.assetBase = opts.assetBase ?? '/assets/Rive/';
  }

  async init(): Promise<void> {
    this.fitAllCanvases();
    window.addEventListener('resize', () => this.fitAllCanvases());

    await Promise.all([
      this.loadOne('main'),
      this.loadOne('topLayer'),
      this.loadOne('betting'),
    ]);

    this.dumpInputs();
    this.emit('ready', {});
    this.emit('allControllersReady', {});
  }

  private fitAllCanvases(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const canvas of Object.values(this.canvases)) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
    }
    for (const t of this.targets.values()) {
      try {
        t.instance.resizeDrawingSurfaceToCanvas();
      } catch {
        /* ignore */
      }
    }
  }

  private loadOne(key: TargetKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const file = FILES[key];
      const artboard = ARTBOARDS[key];
      const sm = STATE_MACHINES[key];
      const canvas = this.canvases[key];
      const src = `${this.assetBase}${file}`;
      const fit = key === 'betting' ? Fit.Cover : Fit.Contain;
      const rive = new Rive({
        canvas,
        src,
        artboard,
        stateMachines: sm,
        autoplay: true,
        layout: new Layout({ fit, alignment: Alignment.Center }),
        onLoad: () => {
          try {
            const inputs = this.cacheInputs(rive, sm);
            const target: RiveTarget = {
              key,
              file,
              stateMachine: sm,
              instance: rive,
              inputs,
            };
            this.targets.set(key, target);
            this.attachEventBridge(rive, key);
            try {
              rive.resizeDrawingSurfaceToCanvas();
            } catch {
              /* ignore */
            }
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onLoadError: (err: unknown) => {
          reject(
            new Error(`Failed to load ${src} [${artboard}/${sm}]: ${String(err)}`),
          );
        },
      });
    });
  }

  private cacheInputs(rive: Rive, sm: string): Map<string, AnyInput> {
    const out = new Map<string, AnyInput>();
    let list: unknown[] = [];
    try {
      list = rive.stateMachineInputs(sm) ?? [];
    } catch (e) {
      warn('[Rive] stateMachineInputs failed for', sm, e);
      return out;
    }
    for (const raw of list) {
      const inp = raw as {
        name: string;
        type: number;
        value: number | boolean;
        fire(): void;
      };
      switch (inp.type) {
        case StateMachineInputType.Number:
          out.set(inp.name, {
            type: 'number',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Number(inp.value ?? 0),
          });
          break;
        case StateMachineInputType.Boolean:
          out.set(inp.name, {
            type: 'boolean',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Boolean(inp.value),
          });
          break;
        case StateMachineInputType.Trigger:
          out.set(inp.name, {
            type: 'trigger',
            name: inp.name,
            fire: () => inp.fire(),
          });
          break;
        default:
          debugLog('[Rive] unknown input type', inp.type, 'for', inp.name);
      }
    }
    return out;
  }

  private dumpInputs(): void {
    for (const t of this.targets.values()) {
      const rows = Array.from(t.inputs.values()).map((i) => ({
        target: t.key,
        file: t.file,
        sm: t.stateMachine,
        name: i.name,
        type: i.type,
      }));
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `[Rive] ${t.key} (${t.file} / ${t.stateMachine}) — ${rows.length} input(s)`,
      );
      // eslint-disable-next-line no-console
      console.table(rows);
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  private attachEventBridge(rive: Rive, target: TargetKey): void {
    try {
      rive.on(EventType.RiveEvent, (event: unknown) => {
        const e = event as {
          data?: { name?: string; properties?: Record<string, unknown> };
        };
        const name = e?.data?.name;
        if (!name) return;
        const props = { ...(e?.data?.properties ?? {}), __target: target };
        if (this.listeners.has(name)) {
          this.emit(name, props);
        } else {
          debugLog('[Rive][event:passthrough]', target, name, props);
        }
      });
    } catch (e) {
      warn('[Rive] event bridge attach failed', e);
    }
  }

  // -----------------------------------------------------------------
  // Public API — mirrors RiveControllerManager
  // -----------------------------------------------------------------

  /**
   * Set the BetPanel state on the topLayer artboard.
   *   1 = startBet popup
   *   2 = noMoreBet popup
   * Mirrors rive_controller_manager.dart:433-447.
   *
   * In V2 these are typically not invoked from the orchestrator — start/no-more
   * popups are rendered as DOM widgets — but the API is preserved for
   * fallback paths.
   */
  fireBetPanel(state: 1 | 2): void {
    this.setNumberAny('topLayer', TopLayerInputs.betPanelNumber, state);
    this.fireTriggerAny('topLayer', TopLayerInputs.betPanelTrigger);
  }

  /**
   * Play the dice bowl "start spin" animation. Mirrors playDiceBowlStart
   * (rive_controller_manager.dart:454-460).
   */
  playDiceBowlStart(): void {
    this.setNumberAny('topLayer', TopLayerInputs.diceBowlPosition, DiceBowlState.start);
    this.fireTriggerAny('topLayer', TopLayerInputs.diceBowlPlay);
  }

  /**
   * Reveal the dice bowl with three dice values (1..6).
   * Mirrors revealDiceBowl (rive_controller_manager.dart:462-475):
   *   1) set DiceNum1/2/3 numbers
   *   2) set DiceBowlPosition = 2 (reveal)
   *   3) fire DiceBowlPlay trigger
   */
  revealDiceBowl(d1: number, d2: number, d3: number): void {
    this.setNumberAny('topLayer', TopLayerInputs.diceNum1, d1);
    this.setNumberAny('topLayer', TopLayerInputs.diceNum2, d2);
    this.setNumberAny('topLayer', TopLayerInputs.diceNum3, d3);
    this.setNumberAny('topLayer', TopLayerInputs.diceBowlPosition, DiceBowlState.reveal);
    this.fireTriggerAny('topLayer', TopLayerInputs.diceBowlPlay);
  }

  /**
   * Spin then reveal the bowl. Mirrors `_riveManager.spinAndReveal`
   * (rive_controller_manager.dart:502-517): start-spin → 1800ms → reveal.
   */
  async spinAndRevealDiceBowl(d1: number, d2: number, d3: number): Promise<void> {
    this.setNumberAny('topLayer', TopLayerInputs.diceNum1, d1);
    this.setNumberAny('topLayer', TopLayerInputs.diceNum2, d2);
    this.setNumberAny('topLayer', TopLayerInputs.diceNum3, d3);
    this.setNumberAny('topLayer', TopLayerInputs.diceBowlPosition, DiceBowlState.start);
    this.fireTriggerAny('topLayer', TopLayerInputs.diceBowlPlay);
    await wait(1800);
    this.setNumberAny('topLayer', TopLayerInputs.diceBowlPosition, DiceBowlState.reveal);
    this.fireTriggerAny('topLayer', TopLayerInputs.diceBowlPlay);
  }

  /** Plays the girl animation. type: 0=idle 1=start 2=end. */
  triggerGirlAnim(type: 0 | 1 | 2): void {
    if (type <= 0) return;
    this.setNumberAny('main', MainInputs.girlAnim, type);
    this.fireTriggerAny('main', MainInputs.trigGirlAnim);
  }

  /** Mirror RiveControllerManager.applySoundSettings (toggles isSound bool). */
  applySoundSettings(soundOn: boolean): void {
    this.setBoolAny('main', MainInputs.isSound, soundOn);
    this.setBoolAny('topLayer', TopLayerInputs.isSound, soundOn);
    this.setBoolAny('betting', 'isSound', soundOn);
  }

  /** Set the betting artboard's `isMouse` flag (desktop hover effects). */
  setIsMouse(on: boolean): void {
    this.setBoolAny('betting', 'isMouse', on);
  }

  setBalanceText(value: string): void {
    this.setTextRunBroadcast(TextInputs.balance, value);
  }
  setUsernameText(value: string): void {
    this.setTextRunBroadcast(TextInputs.username, value);
  }
  /** No-ops in V2 (preserved for orchestrator symmetry). */
  setBetAmountText(_value: string): void {
    /* no-op */
  }
  setBetLimits(_minText: string, _maxText: string): void {
    /* no-op */
  }
  setAllHistory(_history: ReadonlyArray<number>): void {
    /* no-op */
  }
  clearAllHistory(): void {
    /* no-op */
  }
  clearAllHighlights(): void {
    /* no-op */
  }

  setTextRunBroadcast(name: string, value: string): void {
    for (const t of this.targets.values()) {
      try {
        t.instance.setTextRunValue(name, value);
      } catch {
        /* not present on this artboard */
      }
    }
  }

  // -----------------------------------------------------------------
  // Pub/sub
  // -----------------------------------------------------------------

  on(topic: RiveEventTopic | string, cb: RiveEventCallback): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(topic: string, data: Record<string, unknown>): void {
    const set = this.listeners.get(topic);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(data);
      } catch (e) {
        warn('[Rive listener]', e);
      }
    }
  }

  // -----------------------------------------------------------------
  // Low-level
  // -----------------------------------------------------------------

  private setNumberAny(key: TargetKey, name: string, value: number): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'number') {
      inp.set(value);
      return true;
    }
    debugLog(`[Rive] number '${name}' not found on ${key}`);
    return false;
  }

  private setBoolAny(key: TargetKey, name: string, value: boolean): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'boolean') {
      inp.set(value);
      return true;
    }
    return false;
  }

  private fireTriggerAny(key: TargetKey, name: string): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'trigger') {
      inp.fire();
      return true;
    }
    return false;
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export const _UNUSED = { BetPanelState, RiveEventNames };
